import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendarPlus,
  faClock,
  faCheck,
  faTimes,
  faEdit,
} from "@fortawesome/free-solid-svg-icons";
import styles from "./DoctorPortal.module.css";

const SlotManager = ({
  slots = [],
  slotDate,
  setSlotDate,
  generateSlots,
  toggleSlotAvailability,
  editingSlotId,
  startEditSlot,
  cancelEditSlot,
  editedSlotTimes,
  setEditedSlotTimes,
  saveEditedSlot,
}) => {
  function formatTimeToAMPM(time) {
    if (!time) return "";
    const [hours, minutes] = time.split(":");
    let period = "AM";
    let formattedHours = parseInt(hours, 10);
    if (formattedHours >= 12) {
      period = "PM";
      if (formattedHours > 12) formattedHours -= 12;
    }
    return `${formattedHours}:${minutes} ${period}`;
  }

  return (
    <div>
      {/* View Header */}
      <div className={styles.viewHeader}>
        <div>
          <h1 className={styles.viewTitle}>Slot Management</h1>
          <p className={styles.viewSubtitle}>
            Configure clinical consultation windows and manage daily availability
          </p>
        </div>
      </div>

      {/* Date Selector & Generate Bar */}
      <div className={styles.glassCard} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "1.5rem" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "1rem", flexWrap: "wrap" }}>
          <label htmlFor="slotDateInput" style={{ fontWeight: 750, color: "#0f172a", fontSize: "0.95rem" }}>
            Target Date:
          </label>
          <input
            id="slotDateInput"
            type="date"
            value={slotDate}
            onChange={(e) => setSlotDate(e.target.value)}
            className={styles.inputField}
            style={{ width: "auto", minWidth: "180px", fontWeight: 700 }}
          />
        </div>

        <button
          type="button"
          onClick={generateSlots}
          className={styles.primaryBtn}
        >
          <FontAwesomeIcon icon={faCalendarPlus} />
          <span>Generate Slots</span>
        </button>
      </div>

      {/* Slots Grid Presentation */}
      <div style={{ marginTop: "2rem" }}>
        {slots.length === 0 ? (
          <div className={styles.glassCard} style={{ textAlign: "center", padding: "4rem 2rem", color: "#64748b" }}>
            <FontAwesomeIcon icon={faClock} style={{ fontSize: "2.5rem", color: "#cbd5e1", marginBottom: "1rem" }} />
            <h3 style={{ fontSize: "1.25rem", fontWeight: 800, color: "#0f172a", margin: 0 }}>
              No slots configured for {slotDate}
            </h3>
            <p style={{ margin: "6px 0 0 0", fontSize: "0.92rem" }}>
              Click "Generate Slots" above to create standard consultation intervals for this date.
            </p>
          </div>
        ) : (
          <div className={styles.slotGrid}>
            {slots.map((slot) => {
              const isEditing = editingSlotId === slot._id;
              const isBooked = slot.isBooked;

              return (
                <div
                  key={slot._id}
                  className={`${styles.slotCard} ${
                    slot.isAvailable ? styles.slotCardAvailable : styles.slotCardUnavailable
                  }`}
                >
                  {isEditing ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: "8px", width: "100%" }}>
                      <input
                        type="time"
                        value={editedSlotTimes.startTime}
                        onChange={(e) => setEditedSlotTimes({ ...editedSlotTimes, startTime: e.target.value })}
                        className={styles.inputField}
                        style={{ padding: "0.35rem 0.5rem", fontSize: "0.85rem" }}
                      />
                      <input
                        type="time"
                        value={editedSlotTimes.endTime}
                        onChange={(e) => setEditedSlotTimes({ ...editedSlotTimes, endTime: e.target.value })}
                        className={styles.inputField}
                        style={{ padding: "0.35rem 0.5rem", fontSize: "0.85rem" }}
                      />
                      <div style={{ display: "flex", gap: "4px", justifyContent: "center" }}>
                        <button type="button" onClick={() => saveEditedSlot(slot._id)} className={styles.primaryBtn} style={{ padding: "0.3rem 0.6rem", fontSize: "0.75rem" }}>
                          Save
                        </button>
                        <button type="button" onClick={cancelEditSlot} className={styles.ghostBtn} style={{ padding: "0.3rem 0.6rem", fontSize: "0.75rem" }}>
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          flexWrap: "wrap",
                          width: "100%",
                          paddingBottom: "0.6rem",
                          borderBottom: "1px solid rgba(226, 232, 240, 0.8)",
                          marginBottom: "0.2rem",
                          gap: "6px",
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "center", gap: "6px", color: "#0f172a", fontWeight: 850, fontSize: "0.92rem" }}>
                          <FontAwesomeIcon icon={faClock} style={{ color: "#2d6a4f", fontSize: "0.85rem" }} />
                          <span>{formatTimeToAMPM(slot.startTime)} – {formatTimeToAMPM(slot.endTime)}</span>
                        </div>

                        {!isBooked && (
                          <button
                            type="button"
                            onClick={() => startEditSlot(slot._id, slot)}
                            style={{
                              padding: "0.25rem 0.6rem",
                              fontSize: "0.72rem",
                              borderRadius: "6px",
                              background: "#f1f5f9",
                              border: "1px solid #cbd5e1",
                              color: "#334155",
                              fontWeight: 750,
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "4px",
                              cursor: "pointer",
                              transition: "all 0.2s ease",
                              boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
                              outline: "none",
                              marginLeft: "auto",
                            }}
                            onMouseOver={(e) => {
                              e.currentTarget.style.background = "#2d6a4f";
                              e.currentTarget.style.color = "#ffffff";
                              e.currentTarget.style.borderColor = "#2d6a4f";
                            }}
                            onMouseOut={(e) => {
                              e.currentTarget.style.background = "#f1f5f9";
                              e.currentTarget.style.color = "#334155";
                              e.currentTarget.style.borderColor = "#cbd5e1";
                            }}
                            title="Edit Consultation Timings"
                          >
                            <FontAwesomeIcon icon={faEdit} />
                            <span>Edit</span>
                          </button>
                        )}
                      </div>

                      {/* Pill Toggle Button */}
                      <button
                        type="button"
                        disabled={isBooked}
                        onClick={() => toggleSlotAvailability(slot._id, !slot.isAvailable)}
                        style={{
                          width: "100%",
                          padding: "0.5rem",
                          borderRadius: "9999px",
                          border: "none",
                          fontWeight: 750,
                          fontSize: "0.82rem",
                          cursor: isBooked ? "not-allowed" : "pointer",
                          background: isBooked ? "#e2e8f0" : slot.isAvailable ? "#2d6a4f" : "#ef4444",
                          color: isBooked ? "#64748b" : "#ffffff",
                          transition: "all 0.2s ease",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: "6px",
                          boxShadow: isBooked ? "none" : "0 2px 6px rgba(0,0,0,0.1)",
                        }}
                      >
                        <FontAwesomeIcon icon={slot.isAvailable ? faCheck : faTimes} />
                        <span>{isBooked ? "Booked" : slot.isAvailable ? "Available" : "Unavailable"}</span>
                      </button>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default SlotManager;
